import type { LucideIconProps } from './types'

/**
 * Utility function to create consistent default props for custom icons
 * This ensures all custom icons behave exactly like Lucide icons
 */
export function createIconDefaults(
    props: LucideIconProps = {}
): Required<Pick<LucideIconProps, 'size' | 'color' | 'strokeWidth' | 'absoluteStrokeWidth'>> &
    Partial<LucideIconProps> {
    return {
        size: 24,
        color: 'currentColor',
        strokeWidth: 2,
        absoluteStrokeWidth: false,
        ...props,
    }
}

/**
 * Calculate stroke width for absolute stroke width mode
 * This matches Lucide's internal calculation
 */
export function calculateStrokeWidth(
    strokeWidth: number | string,
    size: number | string,
    absoluteStrokeWidth: boolean = false
): number | string {
    if (!absoluteStrokeWidth) return strokeWidth

    const numericStrokeWidth = Number(strokeWidth)
    const numericSize = Number(size)

    if (isNaN(numericStrokeWidth) || isNaN(numericSize)) {
        return strokeWidth
    }

    return (numericStrokeWidth * 24) / numericSize
}

/**
 * Utility to merge icon classes with consistent patterns
 */
export function mergeIconClasses(
    className?: string,
    ...additionalClasses: (string | undefined)[]
): string | undefined {
    const classes = [className, ...additionalClasses].filter(Boolean)
    return classes.length > 0 ? classes.join(' ') : undefined
}
